<template>
  <q-tab-pane name="contact-data">
    <!-- Address -->
    <div class="row gutter-x-md justify-start">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('address') }}</h6>
        <p class="text-faded">{{ $t('address_hint') }}</p>
      </div>

      <div class="col-md-9 col-12">
        <address-input v-model="formData.address" required expanded hideLabel/>
      </div>
    </div>

    <hr/>

    <!-- Contact Data -->
    <div class="row gutter-x-md justify-start">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('contact') }}</h6>
        <p class="text-faded">{{ $t('contact_hint') }}</p>
      </div>

      <div class="col-md-9 col-12">
        <email-input v-model="formData.email" hideLabel/>

        <phone-input v-model="formData.phone" hideLabel/>
      </div>
    </div>

    <hr/>

    <!-- Bank Details -->
    <div class="row gutter-x-md justify-start">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('bank_details') }}</h6>
      </div>

      <div class="col-md-9 col-12">
        <bank-details-input v-model="formData.bankDetails" expanded hideLabel/>
      </div>
    </div>

    <div class="row justify-end">
      <q-btn type="submit" color="primary" @click="handleSave">{{ $t('save') }}</q-btn>
    </div>
  </q-tab-pane>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import AddressInput from 'components/Common/Addressing/AddressInput.vue'
  import BankDetailsInput from 'components/Common/Addressing/BankDetailsInput.vue'
  import CustomerAddress from 'src/utils/SubResources/CustomerAddress'
  import EmailInput from 'components/Common/Addressing/EmailInput.vue'
  import PhoneInput from 'components/Common/Addressing/PhoneInput.vue'
  import changeCase from 'change-case-object'
  import { createErrorFromResponse } from 'src/utils/error'
  import { isEqual } from 'lodash'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        formData: {
          address: CustomerAddress.empty(),
          email: {
            email: '',
            label: null
          },
          phone: {
            number: '',
            label: null
          },
          bankDetails: {
            accountNumber: '',
            accountOwner: '',
            bankName: '',
            bankIdentifier: '',
            label: null
          }
        }
      }
    },

    computed: mapGetters(['team']),

    methods: {
      ...mapActions([
        'updateTeamAddress',
        'updateTeamEmailAddress',
        'updateTeamPhoneNumber',
        'updateTeamBankDetails',
        'createTeamAddress',
        'createTeamEmailAddress',
        'createTeamPhoneNumber',
        'createTeamBankDetails',
        'deleteTeamEmailAddress',
        'deleteTeamPhoneNumber',
        'deleteTeamBankDetails'
      ]),

      handleSave () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              return
            }

            return this.storeChanges()
          })
      },

      storeChanges () {
        return Promise.all([
          this.storeAddressChanges(),
          this.storeEmailAddressChanges(),
          this.storePhoneNumberChanges(),
          this.storeBankDetailsChanges()
        ]).then(() => {
          this.$q.notify({ message: this.$t('team_settings_save_success'), type: 'positive' })
        }).catch(createErrorFromResponse)
      },

      storeAddressChanges () {
        if (!this.changed(this.formData.address, this.team.settings.default_address, 'address_line_1')) {
          return Promise.resolve()
        }

        // Check if we have to create a new address
        let method = this.team.settings.default_address ? this.updateTeamAddress : this.createTeamAddress

        return method({
          teamId: this.team.id,
          addressId: this.formData.address.id,
          data: changeCase.snake(this.formData.address)
        })
      },

      storeEmailAddressChanges () {
        if (!this.changed(this.formData.email, this.team.settings.default_email_address, 'email')) {
          return Promise.resolve()
        }

        // Check if the user wants to delete the email address
        if (!this.formData.email.email) {
          return this.deleteTeamEmailAddress({ teamId: this.team.id, emailAddressId: this.formData.email.id })
        }

        // Check if we have to create a new address
        let method = this.team.settings.default_email_address ? this.updateTeamEmailAddress : this.createTeamEmailAddress

        return method({
          teamId: this.team.id,
          emailAddressId: this.formData.email.id,
          data: changeCase.snake(this.formData.email)
        })
      },

      storePhoneNumberChanges () {
        if (!this.changed(this.formData.phone, this.team.settings.default_phone_number, 'number')) {
          return Promise.resolve()
        }

        // Check if the user wants to delete the phone number
        if (!this.formData.phone.number) {
          return this.deleteTeamPhoneNumber({ teamId: this.team.id, phoneNumberId: this.formData.phone.id })
        }

        // Check if we have to create a new phone number
        let method = this.team.settings.default_phone_number ? this.updateTeamPhoneNumber : this.createTeamPhoneNumber

        return method({
          teamId: this.team.id,
          phoneNumberId: this.formData.phone.id,
          data: changeCase.snake(this.formData.phone)
        })
      },

      storeBankDetailsChanges () {
        if (!this.changed(this.formData.bankDetails, this.team.settings.default_bank_details, 'account_number')) {
          return Promise.resolve()
        }

        // Check if the user wants to delete the bank details
        if (!this.formData.bankDetails.accountNumber) {
          return this.deleteTeamBankDetails({ teamId: this.team.id, bankDetailsId: this.formData.bankDetails.id })
        }

        // Check if we have to create a new address
        let method = this.team.settings.default_bank_details ? this.updateTeamBankDetails : this.createTeamBankDetails

        return method({
          teamId: this.team.id,
          bankDetailsId: this.formData.bankDetails.id,
          data: changeCase.snake(this.formData.bankDetails)
        })
      },

      changed (newObject, originalObject, comparisonKey) {
        // First case: check if some values have changed
        // Second case: checks if the input was empty before and still is now
        return !isEqual(changeCase.snake(newObject), changeCase.snake(originalObject)) &&
          !(!changeCase.snake(newObject)[comparisonKey] && originalObject === null)
      },

      refreshFormData () {
        // We have to assign individually, since Object.assign does not deep merge
        Object.assign(this.formData.address,
                      this.team.settings.default_address ? changeCase.camel(this.team.settings.default_address) : {}
        )
        Object.assign(this.formData.email,
                      this.team.settings.default_email_address ? changeCase.camel(this.team.settings.default_email_address) : {}
        )
        Object.assign(this.formData.phone,
                      this.team.settings.default_phone_number ? changeCase.camel(this.team.settings.default_phone_number) : {}
        )
        Object.assign(this.formData.bankDetails,
                      this.team.settings.default_bank_details ? changeCase.camel(this.team.settings.default_bank_details) : {}
        )
      }
    },

    created () {
      this.refreshFormData()
    },

    components: {
      AddressInput,
      BankDetailsInput,
      EmailInput,
      PhoneInput
    }
  }
</script>
